import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeCheck } from '@fortawesome/pro-light-svg-icons/faBadgeCheck';
import { faCircleQuestion } from '@fortawesome/pro-light-svg-icons/faCircleQuestion';
import { faGlobe } from '@fortawesome/pro-light-svg-icons/faGlobe';
import { TypedMemo } from '../../common/types';
import { oauthConsentMessages as messages } from './oauthConsentMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole consent page; each part uses its own slice of it */
import styles from './OAuthConsentView.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Who the client really is, as `oauthConsent.getConsentInfo` answers it (final review I3). */
export interface OAuthConsentClientIdentityData {
  clientKind: 'registered' | 'cimd';
  clientHost: string | null;
  redirectHost: string;
  knownHost: boolean;
  nameLooksLikeKnownApp: boolean;
}

/**
 * The lines under the title (design: oauth-consent a1, c1, c2, `.oc-id`): a badge, the host that
 * stands behind the client, and the host the browser goes back to. The name is chosen by the
 * client; these lines are not. The server decides every fact here.
 *
 * A DCR client (no client host) on a known redirect host is verified by that host: the code goes
 * only there (re-review N2), so the real ChatGPT, which connects through DCR, reads as verified.
 */
const OAuthConsentClientIdentityComponent: React.FC<{ identity: OAuthConsentClientIdentityData }> = ({ identity }) => {
  const intl = useIntl();
  const { clientHost, redirectHost, knownHost } = identity;
  const isUnverified = clientHost === null && !knownHost;
  // The host next to the badge: the client id host, or the redirect host that verifies a DCR client.
  const shownHost = clientHost ?? (knownHost ? redirectHost : null);

  const badge = knownHost
    ? { icon: faBadgeCheck, className: styles.badgeVerified, label: messages.badgeVerified }
    : isUnverified
      ? { icon: faCircleQuestion, className: styles.badgeUnverified, label: messages.badgeUnverified }
      : { icon: faGlobe, className: undefined, label: messages.badgeAppFrom };

  const line = clientHost !== null ? messages.redirect : knownHost ? messages.knownRedirect : messages.unverifiedLine;

  return (
    <div className={styles.identity}>
      <div className={styles.identityRow}>
        <span className={clsx(styles.badge, badge.className)}>
          <FontAwesomeIcon icon={badge.icon} className={styles.badgeIcon} />
          {intl.formatMessage(badge.label)}
        </span>
        {shownHost !== null && <span className={styles.host}>{shownHost}</span>}
      </div>
      <p className={styles.identityLine}>
        {withBrandWord(
          // The host comes from the client: an element value keeps it plain text.
          intl.formatMessage(line, { host: <React.Fragment key="host">{redirectHost}</React.Fragment> })
        )}
      </p>
    </div>
  );
};

export const OAuthConsentClientIdentity = TypedMemo(OAuthConsentClientIdentityComponent);
